import React, { useState, useEffect } from "react";
import { Pencil, X, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

/* Kleines, dezentes Stift-Icon — nur fuer Super-Admin / KEWAG sichtbar.
   Standard: absolut oben rechts in der Card. Mit className ueberschreibbar (z. B. "static"). */
export const EditPencil = ({ onClick, testid, accent = "#A3E635", className = "" }) => {
  const { t } = useI18n();
  return (
    <button onClick={onClick} data-testid={testid} title={t("Bearbeiten")} type="button"
      className={`z-20 w-8 h-8 rounded-lg flex items-center justify-center bg-white/5 border border-white/10 text-gray-400 hover:text-white transition-colors ${className.includes("static") || className.includes("relative") ? "" : "absolute top-3.5 right-3.5"} ${className}`}
      style={{ "--c": accent }}
      onMouseEnter={(e) => { e.currentTarget.style.color = accent; e.currentTarget.style.borderColor = `${accent}66`; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = ""; e.currentTarget.style.borderColor = ""; }}>
      <Pencil className="w-4 h-4" />
    </button>
  );
};

const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-white/30 transition-colors";

const Field = ({ f, value, onChange, accent }) => {
  const { t } = useI18n();
  const common = { "data-testid": `edit-field-${f.key}`, className: inputCls, value: value ?? "", onChange: (e) => onChange(f.key, e.target.value) };
  return (
    <div>
      <label className="text-xs text-gray-400 mb-1 block">{t(f.label)}{f.required && <span className="text-red-400"> *</span>}</label>
      {f.type === "select" ? (
        <select {...common} style={{ color: "#fff" }}>
          {(f.options || []).map((o) => <option key={o.value} value={o.value} style={{ color: "#000" }}>{t(o.label)}</option>)}
        </select>
      ) : f.type === "textarea" ? (
        <textarea {...common} rows={f.rows || 3} placeholder={f.placeholder ? t(f.placeholder) : ""} />
      ) : f.type === "color" ? (
        <div className="flex items-center gap-2">
          <input type="color" data-testid={`edit-field-${f.key}`} value={value || "#A3E635"} onChange={(e) => onChange(f.key, e.target.value)} className="w-10 h-9 rounded-lg bg-black/40 border border-white/10 cursor-pointer p-0.5" />
          <input className={inputCls} value={value ?? ""} onChange={(e) => onChange(f.key, e.target.value)} placeholder="#A3E635" />
        </div>
      ) : (
        <input type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "url" ? "url" : "text"} {...common} placeholder={f.placeholder ? t(f.placeholder) : ""} />
      )}
      {f.hint && <p className="text-[11px] text-gray-500 mt-1">{t(f.hint)}</p>}
    </div>
  );
};

/* Schema-getriebener Bearbeiten-Dialog. fields: [{key,label,type,options,placeholder,hint,required}]. */
export const EditDialog = ({ open, onClose, title, accent = "#A3E635", fields = [], initial = {}, onSave, testid = "edit-dialog", successMsg }) => {
  const { t } = useI18n();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(initial || {}); /* eslint-disable-next-line */ }, [open]);
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    const missing = fields.find((f) => f.required && !String(form[f.key] ?? "").trim());
    if (missing) return toast.error(t("Bitte Pflichtfelder ausfüllen."));
    setSaving(true);
    try {
      await onSave(form);
      toast.success(successMsg ? t(successMsg) : t("Änderungen gespeichert"));
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Speichern fehlgeschlagen"));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={saving ? undefined : onClose} />
      <div className="relative w-full max-w-md bg-[#16161c] border border-white/10 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#16161c]">
          <h3 className="font-bold flex items-center gap-2"><Pencil className="w-4 h-4" style={{ color: accent }} /> {t(title)}</h3>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white" data-testid="edit-dialog-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3.5">
          {fields.map((f) => <Field key={f.key} f={f} value={form[f.key]} onChange={set} accent={accent} />)}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white disabled:opacity-50">{t("Abbrechen")}</button>
            <button onClick={submit} disabled={saving} data-testid="edit-dialog-save" className="font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 text-black" style={{ background: accent }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("Speichern")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* Hilfsfunktion: ISO/Datum -> YYYY-MM-DD fuer <input type=date>. */
export const toDateInput = (s) => { try { return s ? new Date(s).toISOString().slice(0, 10) : ""; } catch { return ""; } };
